<template>
  <baidu-map
    id="baidu_map"
    ref="map"
    class="bm-view"
    :map-click="!isDrawingPolygon"
    :scroll-wheel-zoom="allowMouseScrollZooming"
    :high-resolution="true"
    :continuous-zoom="true"
    :max-zoom="maxZoom"
    :min-zoom="minZoom"
    :center="center"
    :zoom="zoom"
    @ready="handlerBMap">
    <bm-scale anchor="BMAP_ANCHOR_BOTTOM_LEFT" />
    <div
      v-if="isLoading"
      class="map-loader-container">
      <div>
        <loader-small />
        <span class="ml-3">{{ capitalize($t('messages.initializingMap')) }}</span>
      </div>
    </div>
    <bm-map-type
      v-if="enableMapStyleControl"
      :map-types="['BMAP_NORMAL_MAP', 'BMAP_HYBRID_MAP']"
      anchor="BMAP_ANCHOR_BOTTOM_RIGHT" />
    <bm-navigation anchor="BMAP_ANCHOR_TOP_RIGHT" />
    <div class="map-control-btn-group">
      <button
        v-if="enableMapPointControl"
        class="map-control-btn"
        :class="isSelectingLocation ? 'map-control-btn--active' : ''"
        @click="toggleSelectPoint()">
        <svg
          version="1.1"
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg">
          \n
          <path
            d="m79.996 29.512c0-16.301-13.402-29.512-29.93-29.512-2.0938 0-4.1328 0.21484-6.1055 0.62109-0.035157 0.003906-0.0625 0.011718-0.097657 0.019531-9.7383 2.0273-17.723
            8.7148-21.453 17.586-0.3125 0.72656-0.57422 1.4766-0.82422 2.2266-0.035157 0.10156-0.078126 0.19531-0.10938 0.29297-0.94531 2.9219-1.4727 6.0273-1.4727 9.2617 0
            14.535 10.34 26.652 24.062 29.402l1.9688 36.289c0 1.5977 1.625 4.3008 3.6406 4.3008h0.99219c2.0117 0 3.6445-2.6992 3.6445-4.293l2.293-36.457c13.383-3.0078 23.391-14.949
            23.391-29.242 0-0.078124-0.011719-0.15234-0.011719-0.23047 0.003906-0.09375 0.011719-0.17969 0.011719-0.26562z" />
        </svg>
      </button>
      <button
        v-if="enableMapPolygonControl"
        class="map-control-btn"
        :class="isDrawingPolygon ? 'map-control-btn--active' : ''"
        @click="togglePolygon()">
        <svg
          version="1.1"
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg">
          <g stroke-miterlimit="10">
            <path
              transform="scale(.69444)"
              d="m26.888 37.508l81.439-21.476"
              fill="none"
              stroke-linecap="round"
              stroke-width="8" />
            <path
              transform="scale(.69444)"
              d="m108.33 17.156l18.051 109.14"
              fill="none"
              stroke-linecap="round"
              stroke-width="8" />
            <path
              transform="scale(.69444)"
              d="m126.38 127.39l-100.61-21.403"
              fill="none"
              stroke-linecap="round"
              stroke-width="8" />
            <path
              transform="scale(.69444)"
              d="m24.311 105.4l2.9081-66.921"
              fill="none"
              stroke-linecap="round"
              stroke-width="8" />
            <path
              transform="scale(.69444)"
              d="m110.2 109.04h27.838v26.674h-27.838z"
              stroke-linejoin="bevel"
              stroke-width="2" />
            <path
              transform="scale(.69444)"
              d="m115.96 113.89h17.308v16.054h-17.308z"
              stroke-linejoin="bevel"
              stroke-width="2" />
            <path
              transform="scale(.69444)"
              d="m94.41 3.8194h27.838v26.674h-27.838z"
              stroke-linejoin="bevel"
              stroke-width="2" />
            <path
              transform="scale(.69444)"
              d="m100.18 8.6738h17.308v16.054h-17.308z"
              stroke-linejoin="bevel"
              stroke-width="2" />
            <path
              transform="scale(.69444)"
              d="m13.298 92.059h27.838v26.674h-27.838z"
              stroke-linejoin="bevel"
              stroke-width="2" />
            <path
              transform="scale(.69444)"
              d="m19.063 96.913h17.308v16.054h-17.308z"
              stroke-linejoin="bevel"
              stroke-width="2" />
            <path
              transform="scale(.69444)"
              d="m12.971 25.138h27.838v26.674h-27.838z"
              stroke-linejoin="bevel"
              stroke-width="2" />
            <path
              transform="scale(.69444)"
              d="m18.737 29.992h17.308v16.054h-17.308z"
              stroke-linejoin="bevel"
              stroke-width="2" />
          </g>
        </svg>
      </button>
      <button
        v-show="polygon != null && allowClearGeofence"
        class="map-delete-btn"
        @click="deletePolygon()">
        {{ capitalize($t('actions.clearGeofence')) }}
      </button>
    </div>
    <bm-info-window
      :position="infoWindow.position"
      :show="infoWindow.show"
      @close="infoWindowClose"
      @open="infoWindowOpen">
      <div id="place_popup_content_baidu">
        <div class="place-popup-content-baidu">
          <icon-colored-info />
          <div
            v-if="activeGeofence !== null"
            class="name-and-description-baidu">
            <strong>{{ activeGeofence.name }}</strong>
            <span>{{ activeGeofence.description }}</span>
          </div>
        </div>
      </div>
    </bm-info-window>
  </baidu-map>
</template>

<script>
  import geojsonExtent from '@mapbox/geojson-extent';
  import turfCentroid from '@turf/centroid';
  import { capitalize } from 'vue';

  import Colors from '@/Colors';
  import { LoaderSmall } from '@/common/components/controls';
  import { IconColoredInfo } from '@/common/icons/colored';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';

  export default {
    components: {
      IconColoredInfo,
      LoaderSmall,
    },
    props: {
      initialPolygon: {
        type: Object,
        default: null,
      },
      allowMouseScrollZooming: {
        type: Boolean,
        default: false,
      },
      allowClearGeofence: {
        type: Boolean,
        default: false,
      },
      enableMapPointControl: {
        type: Boolean,
        default: false,
      },
      enableMapPolygonControl: {
        type: Boolean,
        default: false,
      },
      enableMapStyleControl: {
        type: Boolean,
        default: false,
      },
      geofences: {
        type: Array,
        default: null,
      },
      searchProximity: {
        type: Object,
        default: null,
      },
      activeGeofence: {
        type: Object,
        default: null,
      },
      clearNewPolygon: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        mapZoom: 13,
        center: null,
        zoom: 1,
        BMap: null,
        map: null,
        isLoading: true,
        marker: null,
        isSelectingLocation: false,
        isDrawingPolygon: false,
        polygonPath: [],
        polygon: null,
        isSatellite: false,
        maxZoom: 19,
        minZoom: 4,
        bounds: [],
        infoWindow: {
          show: false,
          position: null,
        },
        markers: [],
        polygons: [],
        localSearch: null,
        point: null,
        localSearchForPolygon: null,
        polygonCoords: {},
        radiusCircles: [],
        placeSearchLoading: false,
        closestPolygonPointToClickCoordinates: null,
      };
    },
    watch: {
      activeGeofence() {
        if (this.activeGeofence != null) {
          this.bounds = [
            {
              lng: this.activeGeofence.boundsSwLng,
              lat: this.activeGeofence.boundsSwLat,
            },
            {
              lng: this.activeGeofence.boundsNeLng,
              lat: this.activeGeofence.boundsNeLat,
            },
          ];
          this.fitToBounds();
        } else {
          this.infoWindowClose();
          this.shapesBoundingBox();
        }
      },
      geofences() {
        if (this.geofences != null && this.geofences.length > 0 && this.map != null) {
          this.updatePointsAndPolygons();
        } else {
          this.deleteAll();
        }
      },
      clearNewPolygon() {
        if (this.clearNewPolygon) {
          if (this.polygon != null) {
            this.map.removeOverlay(this.polygon);
            this.polygon = null;
            this.isDrawingPolygon = false;
            this.polygonPath = [];
            this.updatePointsAndPolygons();
            this.closestPolygonPointToClickCoordinates = null;
            this.$emit('polygonCleared');
          }
        }
      },
      placeSearchLoading(newValue) {
        if (newValue) {
          GlobalEventBus.$emit(GlobalEvents.loading);
        } else {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
      },
    },
    methods: {
      capitalize,
      handlerBMap({ BMap, map }) {
        this.isLoading = false;
        this.BMap = BMap;
        this.map = map;
        if (this.geofences != null && this.geofences.length > 0) {
          this.updatePointsAndPolygons();
        } else {
          this.map.centerAndZoom('中国');
        }
        if (this.activeGeofence != null) {
          this.bounds = [
            {
              lng: this.activeGeofence.boundsSwLng,
              lat: this.activeGeofence.boundsSwLat,
            },
            {
              lng: this.activeGeofence.boundsNeLng,
              lat: this.activeGeofence.boundsNeLat,
            },
          ];
          this.fitToBounds();
        }
      },
      updatePointsAndPolygons() {
        this.map.clearOverlays();

        this.geofences.forEach((geofence) => {
          const icon = new this.BMap.Icon('/assets/images/map/pin-x1.png', new this.BMap.Size(12, 12), {
            anchor: new this.BMap.Size(6, 6),
            infoWindowAnchor: new this.BMap.Size(6, 0),
          });

          const pointCoords = new this.BMap.Point(geofence.lng, geofence.lat);

          const circle = new this.BMap.Marker(pointCoords, {
            icon,
          });

          circle.addEventListener('click', () => {
            this.onPointClicked(geofence);
          });
          this.map.addOverlay(circle);
          this.markers.push(circle);

          if (geofence.isPoint) {
            const radiusCircle = new this.BMap.Circle(pointCoords, geofence.radius * 1000, {
              strokeColor: Colors.geofences,
              fillColor: Colors.geofences,
              strokeWeight: 1,
              strokeOpacity: 0.8,
              fillOpacity: 0.2,
              strokeStyle: 'solid',
            });
            this.map.addOverlay(radiusCircle);
            this.radiusCircles.push(radiusCircle);
          }

          if (geofence.geoJson != null && !geofence.isPoint) {
            let geofencePolygonPath = [];
            if (typeof geofence.geoJson === 'string') {
              // drawing multiple polygons at one location when a polygon is appended
              const geofenceObject = JSON.parse(geofence.geoJson);
              geofenceObject.coordinates.forEach((polygon) => {
                geofencePolygonPath = polygon[0].map((point) => new this.BMap.Point(point[0], point[1]));
                const geofencePolygon = new this.BMap.Polygon(geofencePolygonPath, {
                  strokeColor: Colors.geofences,
                  fillColor: Colors.geofences,
                  strokeWeight: 1,
                  strokeOpacity: 0.8,
                  fillOpacity: 0.2,
                  strokeStyle: 'solid',
                });
                this.map.addOverlay(geofencePolygon);
                this.polygons.push(geofencePolygon);
              });
            } else {
              if ('geometry' in geofence.geoJson) {
                geofencePolygonPath = geofence.geoJson.geometry.coordinates[0].map(
                  (point) => new this.BMap.Point(point[0], point[1]),
                );
              } else {
                geofencePolygonPath = geofence.geoJson.coordinates[0].map(
                  (point) => new this.BMap.Point(point[0], point[1]),
                );
              }

              const geofencePolygon = new this.BMap.Polygon(geofencePolygonPath, {
                strokeColor: Colors.geofences,
                fillColor: Colors.geofences,
                strokeWeight: 1,
                strokeOpacity: 0.8,
                fillOpacity: 0.2,
                strokeStyle: 'solid',
              });
              this.map.addOverlay(geofencePolygon);
              this.polygons.push(geofencePolygon);
            }
          }
        });
        if (this.activeGeofence != null) {
          this.fitToBounds();
        } else {
          this.shapesBoundingBox();
        }
      },
      deleteAll() {
        this.map.clearOverlays();
      },
      getBounds() {
        return this.map.getViewport();
      },
      addLocationMarker(location) {
        const newMarker = new this.BMap.Marker(location);
        if (this.marker != null) {
          this.map.removeOverlay(this.marker);
        }
        this.mapZoom = this.maxZoom;
        this.mapCenter = location;
        this.map.addOverlay(newMarker);
        this.map.centerAndZoom(this.mapCenter, this.mapZoom);
        this.marker = newMarker;
      },
      toggleSelectPoint() {
        this.isDrawingPolygon = false;
        this.isSelectingLocation = !this.isSelectingLocation;
        if (this.isSelectingLocation) {
          this.map.addEventListener('click', this.selectPoint);
        } else {
          this.map.removeEventListener('click', this.selectPoint);
        }
      },
      async togglePolygon() {
        this.isSelectingLocation = false;
        this.isDrawingPolygon = !this.isDrawingPolygon;
        if (this.isDrawingPolygon) {
          this.oldMaxZoom = this.maxZoom;
          this.oldMinZoom = this.minZoom;
          this.maxZoom = this.map.getZoom();
          this.minZoom = this.map.getZoom();

          this.map.addEventListener('click', this.drawPolygon);
          if (this.polygon != null) {
            this.polygon.enableEditing();
          }
        } else {
          this.maxZoom = this.oldMaxZoom;
          this.minZoom = this.oldMinZoom;
          if (this.polygonPath != null) {
            await this.completePolygonEditing();
          }
        }
      },
      async completePolygonEditing() {
        this.maxZoom = this.oldMaxZoom;
        this.minZoom = this.oldMinZoom;

        this.map.removeEventListener('click', this.drawPolygon);
        Array.from(document.getElementsByClassName('BMap_vectex_node')).forEach((elm) =>
          elm.removeEventListener('mousedown', this.onPolygonPointMouseDown),
        );

        if (this.polygon != null) {
          this.polygonPath = [];
          this.polygon.disableEditing();
          const coords = {
            type: 'MultiPolygon',
          };
          coords.coordinates = this.polygon.nc.map((point) => [point.zb.lng, point.zb.lat]);
          coords.coordinates.push([this.polygon.nc[0].zb.lng, this.polygon.nc[0].zb.lat]);
          coords.coordinates = [[[...coords.coordinates]]];
          coords.id = this.polygon.ba;
          coords.geometry = {
            type: 'Polygon',
            coordinates: [...coords.coordinates.flat()],
          };
          coords.bounds = this.map.getBounds();

          this.polygonCoords = coords;
          const centroid = turfCentroid(coords);
          const localSearchPoint = new this.BMap.Point(
            centroid.geometry.coordinates[0],
            centroid.geometry.coordinates[1],
          );
          this.localSearchForPolygon = new this.BMap.LocalSearch(this.map, {
            onSearchComplete: this.getLocalResultsForPolygon,
          });

          this.placeSearchLoading = true;
          // wait for Baidu place search to complete
          await this.localSearchForPolygon.searchNearby('商场，企业，公司，写字楼', localSearchPoint, 100);
        }
      },
      getLocalResultsForPolygon() {
        this.placeSearchLoading = false;
        const results = this.localSearchForPolygon.getResults();
        this.$emit('polygonCreated', this.polygonCoords, results);
      },
      async selectPoint(event) {
        if (this.isSelectingLocation) {
          this.point = {
            lng: event.point.lng,
            lat: event.point.lat,
          };
          const point = new this.BMap.Point(event.point.lng, event.point.lat);
          this.localSearch = new this.BMap.LocalSearch(this.map, {
            onSearchComplete: this.getLocalResults,
          });

          this.placeSearchLoading = true;
          // wait for Baidu place search to complete
          await this.localSearch.searchNearby('商场，企业，公司，写字楼', point, 100);
        }
      },
      getLocalResults() {
        const results = this.localSearch.getResults();
        this.isSelectingLocation = false;
        this.placeSearchLoading = false;
        this.$emit('pointAdded', this.point, results);
      },
      deletePolygon() {
        this.map.removeOverlay(this.polygon);
        this.polygon = null;
        this.polygonPath = [];
        this.closestPolygonPointToClickCoordinates = null;
      },
      onPolygonPointMouseDown(event) {
        //
        // Try to find a polygon point when the mouse went down.
        //
        const rect = document.querySelector('.map-container').getBoundingClientRect();
        this.polygonPointMouseDownAt = {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        };
        this.getClosestPolygonPointToClickCoordinates(this.polygonPointMouseDownAt.x, this.polygonPointMouseDownAt.y);
      },
      onPolygonPointMouseUp(event) {
        //
        // Update the screen coordinates of the polygon point that was dragged.
        //
        const rect = document.querySelector('.map-container').getBoundingClientRect();
        this.polygonPointMouseUpAt = {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        };

        this.closestPolygonPointToClickCoordinates.x += this.polygonPointMouseUpAt.x - this.polygonPointMouseDownAt.x;
        this.closestPolygonPointToClickCoordinates.y += this.polygonPointMouseUpAt.y - this.polygonPointMouseDownAt.y;
      },
      onPolygonChanged() {
        const activePointIndex = this.polygonPath.indexOf(this.closestPolygonPointToClickCoordinates);
        if (this.closestPolygonPointToClickCoordinates && activePointIndex > -1) {
          this.polygonPath = this.polygon.getPath();
          this.polygonPath[activePointIndex].x = this.closestPolygonPointToClickCoordinates.x;
          this.polygonPath[activePointIndex].y = this.closestPolygonPointToClickCoordinates.y;
          this.polygon.setPath(this.polygonPath);
        }
      },
      getClosestPolygonPointToClickCoordinates(x, y) {
        const successfulHitTestMaxDistanceInPixels = 6;
        this.closestPolygonPointToClickCoordinates = this.polygonPath.find(
          (p) =>
            p.x >= x - successfulHitTestMaxDistanceInPixels &&
            p.x <= x + successfulHitTestMaxDistanceInPixels &&
            p.y <= y + successfulHitTestMaxDistanceInPixels &&
            p.y <= y + successfulHitTestMaxDistanceInPixels,
        );
      },
      drawPolygon(event) {
        if (this.isDrawingPolygon) {
          //
          // If you clicked on the first point of the polygon, end the editing.
          //
          const didClickOnFirstPolygonPoint =
            this.closestPolygonPointToClickCoordinates != null &&
            this.polygonPath.indexOf(this.closestPolygonPointToClickCoordinates) === 0;

          if (this.closestPolygonPointToClickCoordinates) {
            if (didClickOnFirstPolygonPoint) {
              this.completePolygonEditing();
            }
          } else {
            // Handle polygon 'lineupdate', this occurs when dragging existing points.
            if (this.polygonPath.length > 0) {
              this.polygon.removeEventListener('lineupdate', this.onPolygonChanged);
              this.map.removeOverlay(this.polygon);
            }

            this.polygonPath.push({
              lng: event.point.lng,
              lat: event.point.lat,
              x: event.offsetX, // Used for hit testing
              y: event.offsetY, // Used for hit testing
            });

            // Remove event handlers on all points of the polygon.
            Array.from(document.getElementsByClassName('BMap_vectex_node')).forEach((elm) => {
              elm.removeEventListener('mousedown', this.onPolygonPointMouseDown);
              elm.removeEventListener('mouseup', this.onPolygonPointMouseUp);
            });

            const polygonPoints = this.polygonPath.map((point) => new this.BMap.Point(point.lng, point.lat));
            this.polygon = new this.BMap.Polygon(polygonPoints, {
              strokeColor: Colors.geofences,
              fillColor: Colors.geofences,
              strokeWeight: 1,
              strokeOpacity: 0.8,
              fillOpacity: 0.2,
              strokeStyle: 'dashed',
              enableEditing: true,
            });

            this.polygon.addEventListener('lineupdate', this.onPolygonChanged);

            this.map.addOverlay(this.polygon);

            // Add event handlers on all points of the polygon.
            Array.from(document.getElementsByClassName('BMap_vectex_node')).forEach((elm) => {
              elm.addEventListener('mousedown', this.onPolygonPointMouseDown);
              elm.addEventListener('mouseup', this.onPolygonPointMouseUp);
            });
          }
        }
      },
      shapesBoundingBox() {
        const data = {
          type: 'FeatureCollection',
          features: this.geofences != null ? this.geofences.map((x) => x.feature) : [],
        };
        if (data != null && data.features != null && data.features.length !== 0) {
          const extents = data.features.map((x) => geojsonExtent(x.geometry));

          const minLat = Math.min(...extents.map((x) => x[1]));
          const maxLat = Math.max(...extents.map((x) => x[3]));
          const minLng = Math.min(...extents.map((x) => x[0]));
          const maxLng = Math.max(...extents.map((x) => x[2]));

          this.bounds = [
            {
              lng: minLng,
              lat: minLat,
            },
            {
              lng: maxLng,
              lat: maxLat,
            },
          ];

          if (!this.map) return;

          this.fitToBounds();
          const viewport = this.map.getViewport(this.bounds);
          this.mapCenter = viewport.center;
          this.map.centerAndZoom(this.mapCenter);
        }
      },
      fitToBounds() {
        if (!this.map) return;
        this.map.setViewport(this.bounds);
      },
      validateBounds() {
        return this.bounds.some((bound) => Math.abs(bound.lat) === Infinity || Math.abs(bound.lng) === Infinity);
      },
      onPointClicked(geofence) {
        this.infoWindow.position = {
          lng: geofence.lng,
          lat: geofence.lat,
        };
        this.infoWindowOpen();
        this.$emit('pointClicked', geofence);
      },
      infoWindowClose() {
        this.infoWindow.show = false;
      },
      infoWindowOpen() {
        this.infoWindow.show = true;
      },
      updateGeofenceDataSources() {
        // Placeholder
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .bm-view {
    margin: auto;
    margin: 0 -1.6rem 0 -1.6rem;
    height: 40rem;

    .map-loader-container {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      display: flex;
      z-index: 1;

      > div {
        margin: auto;
        padding: 1.6rem 2.4rem;
        background: #fff;
        box-shadow: $small-shadow;
        border-radius: 0.8rem;
        position: relative;
        display: flex;
        align-items: center;
      }
    }

    .map-control-btn-group {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-end;
      position: absolute;
      right: 3.5rem;
      top: 20rem;

      .map-delete-btn {
        border-radius: 0.4rem;
        border: solid 0.1rem #ccc;
        box-shadow: 0 0.1rem 0.3rem rgba(0, 0, 0, 0.15);
        background: #fff;

        color: var(--secondaryColor);
      }

      .map-control-btn {
        border-radius: 0.4rem;
        border: solid 0.1rem #ccc;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 3rem;
        height: 3rem;
        padding: 0;
        box-shadow: 0 0.1rem 0.3rem rgba(0, 0, 0, 0.15);
        background: #fff;
        z-index: 1;
        margin-bottom: 1rem;

        svg {
          width: 2rem;
          height: 2rem;
          stroke: #000;
          fill: #000;
          margin: auto;
        }

        &--active {
          svg {
            stroke: var(--primaryColor);
            fill: var(--primaryColor);
          }
        }
      }
    }

    .place-popup-content-baidu {
      background-color: #fff;
      display: flex;
      align-items: center;
      max-width: 20rem;
      font-size: 1.2rem;

      svg {
        min-width: 3.2rem;
        max-width: 3.2rem;
        margin-right: 1.2rem;
      }

      .name-and-description-baidu {
        display: flex;
        flex-direction: column;
        line-height: initial;

        strong {
          font-size: 1.2em;
          margin-bottom: 0.6rem;
        }

        .vue-slider-component {
          width: 20rem !important;
        }
      }
    }
  }
</style>
